import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../fonts/breuer-headline.css";
import "./select.css";
import { setBackButtonUrl } from "../uiSlice";
import {
  getDvSourceImageUrl,
  getThemeButtonGradient,
  getThemeButtonTextColor,
} from "../../functions/themeAssets";
import { parseStringList } from "../game/raceGameUtils";
import {
  validateThemeStageData,
  fireThemeStageDataMissingSwal,
} from "../../functions/validateThemeStageData";
import { fetchThemeData } from "../../admin/themeSlice";
import {
  DIFFICULTY_STORAGE_KEY,
  GAME_MODE_STORAGE_KEY,
  SELECTED_CAR_STORAGE_KEY,
  getBackUrlFromSelection,
  getEnabledDifficulties,
  readSessionValue,
  writeSessionValue,
} from "../gameSetupStorage";
import RaceTitleBanner from "../components/RaceTitleBanner";

const QUIZ_MODE_ID = "quiz_runner";
const CAR_SWIPE_MS = 340;
const SWIPE_THRESHOLD_PX = 42;

export default function CarSelection() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);
  const { status, user } = useSelector((s) => s.auth);
  const step = searchParams.get("step") === "controls" ? "controls" : "car";
  const [index, setIndex] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [slideDir, setSlideDir] = useState(null);
  const [animKey, setAnimKey] = useState(0);
  const animLockRef = useRef(false);
  const touchStartRef = useRef(null);
  const isQuizMode = readSessionValue(GAME_MODE_STORAGE_KEY) === QUIZ_MODE_ID;
  const enabledDifficulties = useMemo(
    () => getEnabledDifficulties(themeData),
    [themeData]
  );
  const [difficulty, setDifficulty] = useState(
    () => readSessionValue(DIFFICULTY_STORAGE_KEY) || "medium"
  );

  useEffect(() => {
    const carBack = getBackUrlFromSelection(themeData);
    dispatch(setBackButtonUrl(step === "controls" ? "/selection" : carBack));
  }, [dispatch, status, user, step, themeData]);

  useEffect(() => {
    if (isQuizMode || !enabledDifficulties.length) return;
    const ids = enabledDifficulties.map((d) => d.id);
    if (!ids.includes(difficulty)) {
      const next = ids[0];
      setDifficulty(next);
      writeSessionValue(DIFFICULTY_STORAGE_KEY, next);
    }
  }, [enabledDifficulties, difficulty, isQuizMode]);

  const carUrls = useMemo(() => {
    const files = parseStringList(themeData?.car_images);
    return files.map((f) => getDvSourceImageUrl(f)).filter(Boolean);
  }, [themeData]);

  useEffect(() => {
    if (!carUrls.length) {
      setIndex(0);
      return;
    }
    setIndex((i) => Math.min(i, carUrls.length - 1));
  }, [carUrls]);

  const shiftCars = useCallback(
    (dir) => {
      if (!carUrls.length || carUrls.length < 2 || animLockRef.current) return;
      animLockRef.current = true;
      setSlideDir(dir);
      setAnimKey((k) => k + 1);
      setIndex((i) =>
        dir === "next"
          ? (i + 1) % carUrls.length
          : (i - 1 + carUrls.length) % carUrls.length
      );
      window.setTimeout(() => {
        animLockRef.current = false;
      }, CAR_SWIPE_MS);
    },
    [carUrls.length]
  );

  const goPrev = useCallback(() => shiftCars("prev"), [shiftCars]);
  const goNext = useCallback(() => shiftCars("next"), [shiftCars]);

  const onTrackPointerDown = (e) => {
    if (carUrls.length < 2 || (e.button != null && e.button !== 0)) return;
    touchStartRef.current = { x: e.clientX, y: e.clientY };
  };

  const onTrackPointerUp = (e) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start || carUrls.length < 2) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy) * 1.2) {
      return;
    }
    if (dx < 0) goNext();
    else goPrev();
  };

  const onTrackPointerCancel = () => {
    touchStartRef.current = null;
  };

  const visibleCars = useMemo(() => {
    if (!carUrls.length) return [];
    if (carUrls.length === 1) {
      return [{ url: carUrls[0], slot: "center", realIndex: 0 }];
    }
    if (carUrls.length === 2) {
      return [
        {
          url: carUrls[(index - 1 + carUrls.length) % carUrls.length],
          slot: "left",
          realIndex: (index - 1 + carUrls.length) % carUrls.length,
        },
        { url: carUrls[index], slot: "center", realIndex: index },
      ];
    }
    return [
      {
        url: carUrls[(index - 1 + carUrls.length) % carUrls.length],
        slot: "left",
        realIndex: (index - 1 + carUrls.length) % carUrls.length,
      },
      { url: carUrls[index], slot: "center", realIndex: index },
      {
        url: carUrls[(index + 1) % carUrls.length],
        slot: "right",
        realIndex: (index + 1) % carUrls.length,
      },
    ];
  }, [carUrls, index]);

  const confirmCar = useCallback(() => {
    if (!carUrls.length || confirming) return;
    const fileList = parseStringList(themeData?.car_images);
    const selectedFile = fileList[index] || fileList[0] || "";
    writeSessionValue(SELECTED_CAR_STORAGE_KEY, selectedFile);
    if (!isQuizMode) {
      writeSessionValue(DIFFICULTY_STORAGE_KEY, difficulty);
    }
    setSearchParams({ step: "controls" }, { replace: true });
  }, [carUrls, confirming, themeData, index, setSearchParams, isQuizMode, difficulty]);

  const startGame = useCallback(async () => {
    if (confirming) return;
    setConfirming(true);
    try {
      const freshTheme = await dispatch(
        fetchThemeData({ themeId: null })
      ).unwrap();
      const themePayload = freshTheme?.data ?? themeData;
      const stageCheck = validateThemeStageData(themePayload, {
        mode: readSessionValue(GAME_MODE_STORAGE_KEY),
      });
      if (!stageCheck.ok) {
        await fireThemeStageDataMissingSwal(stageCheck.missing);
        return;
      }
      navigate("/stage1", { replace: true });
    } catch (err) {
      console.error("start race:", err);
      navigate("/stage1", { replace: true });
    } finally {
      setConfirming(false);
    }
  }, [confirming, dispatch, themeData, navigate]);

  const startButtonStyle = useMemo(() => {
    if (!themeData) return undefined;
    const { start, end } = getThemeButtonGradient(themeData);
    const textColor = getThemeButtonTextColor(themeData);
    const style = {};
    if (start && end) {
      style.background = `linear-gradient(135deg, ${start}, ${end})`;
    } else if (start) {
      style.background = start;
    }
    if (textColor) {
      style.color = textColor;
      style.borderColor = textColor;
    }
    return Object.keys(style).length ? style : undefined;
  }, [themeData]);

  if (themeStatus === "loading" && !themeData) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  if (step === "controls") {
    return (
      <div className="select-page">
        <RaceTitleBanner>CONTROLS</RaceTitleBanner>

        {/* Desktop keyboard controls */}
        <div className="controls-panel controls-panel--desktop">
          <div className="controls-col">
            <h2 className="controls-col__title">Arrow keys</h2>
            <div className="controls-keys controls-keys--arrows" aria-hidden>
              <span className="controls-key controls-key--up">↑</span>
              <span className="controls-key controls-key--left">←</span>
              <span className="controls-key controls-key--down">↓</span>
              <span className="controls-key controls-key--right">→</span>
            </div>
          </div>
          <div className="controls-col">
            <h2 className="controls-col__title">WASD Keys</h2>
            <div className="controls-keys controls-keys--wasd" aria-hidden>
              <span className="controls-key controls-key--up">W</span>
              <span className="controls-key controls-key--left">A</span>
              <span className="controls-key controls-key--down">S</span>
              <span className="controls-key controls-key--right">D</span>
            </div>
          </div>
          <div className="controls-col">
            <h2 className="controls-col__title">Space bar</h2>
            <div className="controls-keys controls-keys--space" aria-hidden>
              <span className="controls-key controls-key--space">SPACE</span>
            </div>
            <p className="controls-col__hint">Nitro boost</p>
          </div>
        </div>

        {/* Mobile: on-screen buttons + motion */}
        <div className="controls-panel controls-panel--mobile">
          <div className="controls-col">
            <h2 className="controls-col__title">Arrow buttons</h2>
            <div className="controls-mobile-pad" aria-hidden>
              <span className="controls-mobile-btn">‹</span>
              <span className="controls-mobile-btn controls-mobile-btn--nitro">
                TAP
              </span>
              <span className="controls-mobile-btn">›</span>
            </div>
            <p className="controls-col__hint">
              Steer left / right. Tap the road for Nitro.
            </p>
          </div>
          <div className="controls-col">
            <h2 className="controls-col__title">Motion</h2>
            <div className="controls-mobile-motion" aria-hidden>
              <span className="controls-mobile-phone">
                <span className="controls-mobile-phone__tilt">⌖</span>
              </span>
              <div className="controls-mobile-arrows">
                <span>← tilt</span>
                <span>tilt →</span>
              </div>
            </div>
            <p className="controls-col__hint">
              Tilt to steer. Tap the road for Nitro.
            </p>
          </div>
        </div>

        <button
          type="button"
          className="select-continue-btn"
          style={startButtonStyle}
          onClick={startGame}
          disabled={confirming}
        >
          {confirming ? "Loading…" : "START RACE"}
        </button>
      </div>
    );
  }

  const trackSwipeClass =
    slideDir === "next"
      ? " is-swipe-next"
      : slideDir === "prev"
        ? " is-swipe-prev"
        : "";

  return (
    <div className="select-page">
      <RaceTitleBanner>SELECT YOUR CAR</RaceTitleBanner>

      {!carUrls.length ? (
        <p className="select-empty">
          No car images found. Upload cars in admin Game Content → Game Image.
        </p>
      ) : (
        <div className="car-panel">
          <button
            type="button"
            className="car-nav car-nav--prev"
            onClick={goPrev}
            aria-label="Previous car"
            disabled={carUrls.length < 2}
          >
            ◀
          </button>

          <div
            className={`car-track${trackSwipeClass}`}
            key={animKey}
            role="listbox"
            aria-label="Cars"
            onPointerDown={onTrackPointerDown}
            onPointerUp={onTrackPointerUp}
            onPointerCancel={onTrackPointerCancel}
          >
            {visibleCars.map((item) => (
              <button
                key={`${item.slot}-${item.realIndex}`}
                type="button"
                role="option"
                aria-selected={item.slot === "center"}
                className={`car-slot car-slot--${item.slot}`}
                onClick={() => {
                  if (item.slot === "left") goPrev();
                  else if (item.slot === "right") goNext();
                  else confirmCar();
                }}
              >
                <img
                  src={item.url}
                  alt=""
                  className="car-slot__img"
                  draggable={false}
                />
              </button>
            ))}
          </div>

          <button
            type="button"
            className="car-nav car-nav--next"
            onClick={goNext}
            aria-label="Next car"
            disabled={carUrls.length < 2}
          >
            ▶
          </button>

          <button
            type="button"
            className="car-confirm"
            onClick={confirmCar}
            aria-label="Confirm car"
            title="Confirm car"
          >
            ✓
          </button>
        </div>
      )}
    </div>
  );
}
